import { captureException } from '@sentry/browser';
import axios from 'axios';
import { ProjectSection, ProjectSectionId } from '../core/domain/Project';
import { ExternalProjectPort, RepairSolution } from '../core/project/ExternalProjectPort';

export interface Project {
    id: string;
    name: string;
    organizationId: string;
    createdAt: string;
    updatedAt: string;
}

export class ExternalProjectProvider implements ExternalProjectPort {
    constructor(private readonly baseEndpoint: string) {}

    async loadProjectSections(projectId?: string): Promise<ProjectSection[]> {
        try {
            const effectiveProjectId = projectId ?? (await this.getDefaultProject()).id;
            const url = `${this.baseEndpoint}/project/${effectiveProjectId}/sections`;
            const response = await axios.request({
                method: 'GET',
                url,
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }

    async getDefaultProject(): Promise<Project> {
        try {
            const response = await axios.request({
                method: 'GET',
                url: `${this.baseEndpoint}/project/default`,
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }

    async editProjectSection(
        projectSectionId: ProjectSectionId,
        projectId: string,
        data: Partial<ProjectSection>,
    ): Promise<ProjectSection> {
        try {
            const response = await axios.request({
                method: 'PATCH',
                url: `${this.baseEndpoint}/project/${projectId}/sections/${projectSectionId}`,
                data,
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }

    async editProjectSections(
        projectSectionIds: ProjectSectionId[],
        projectId: string,
        data: Partial<ProjectSection>,
    ): Promise<ProjectSection[]> {
        try {
            const response = await axios.request({
                method: 'PATCH',
                url: `${this.baseEndpoint}/project/${projectId}/sections`,
                data: {
                    projectSectionIds,
                    data,
                },
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }

    async loadProjects(): Promise<Project[]> {
        try {
            const response = await axios.request({
                method: 'GET',
                url: `${this.baseEndpoint}/project`,
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }

    async createProject(name: string): Promise<Project> {
        try {
            const response = await axios.request({
                method: 'POST',
                url: `${this.baseEndpoint}/project`,
                data: { name },
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }

    async loadRepairSolutions(): Promise<RepairSolution[]> {
        try {
            const response = await axios.request({
                method: 'GET',
                url: `${this.baseEndpoint}/project/repair-solutions`,
                withCredentials: true,
            });

            return response.data;
        } catch (err) {
            console.error(err);
            captureException(err);
            throw err;
        }
    }
}
